import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon, XIcon } from 'lucide-react';
import { INSIDE_SUB, INSIDE_TITLE, insidePages } from '../data/halloweenKit';
const EASE = [0.23, 1, 0.32, 1] as const;
const TOTAL = insidePages.length;
const ARROW = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-pumpkin/40 text-pumpkin transition-colors duration-150 ease-out hover:bg-pumpkin hover:text-night-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pumpkin';
export function LookInsideSection() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const go = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const target = (next + TOTAL) % TOTAL;
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(target);
  }, []);

  // Keep the active index in sync with native swipe.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        setIndex(Math.min(Math.max(i, 0), TOTAL - 1));
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  return <section className="w-full bg-night-900 py-14 sm:py-20" aria-labelledby="inside-title" data-id="element-137" data-mp="cm9pd46rtny7">
      <div className="mx-auto max-w-5xl px-5" data-id="element-138" data-mp="29etf7wdszwrz">
        <h2 id="inside-title" className="text-center font-display text-[1.75rem] font-semibold leading-tight text-cream-50 sm:text-[2.5rem]" data-id="element-139" data-mp="29g4rkklbecup">
          {INSIDE_TITLE}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-[15px] leading-relaxed text-body sm:text-[16px]" data-id="element-140" data-mp="26dqisq6xw3j5">
          {INSIDE_SUB}
        </p>

        <div className="mt-8 flex items-center justify-center gap-4" data-id="element-141" data-mp="2gb4p3wz5fbxq">
          <button type="button" onClick={() => go(index - 1)} className={`hidden sm:flex ${ARROW}`} data-id="element-142" data-mp="2g9bhaecsulo5">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-143" data-mp="2beeale2zsoef" />
            <span className="sr-only" data-id="element-144" data-mp="2pa9nn3apn59w">Previous image</span>
          </button>

          <ul ref={trackRef} className="no-scrollbar flex w-full max-w-4xl snap-x snap-mandatory overflow-x-auto" aria-label="Product images" data-id="element-145" data-mp="2kc82mctq4vvs">
            {insidePages.map((page, i) => <li key={page.image.src} className="w-full shrink-0 snap-center" data-id="element-146" data-mp="2u55e3z0atkkw" data-mpi={i}>
                <button type="button" onClick={() => setZoomed(i)} className="block w-full overflow-hidden rounded-2xl border border-pumpkin/25 bg-night-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-pumpkin" data-id="element-147" data-mp="382u0xcdxf1sl">
                  <img src={page.image.src} alt={page.image.alt} width={page.image.width} height={page.image.height} loading={i === 0 ? 'eager' : 'lazy'} className="block h-auto w-full object-contain" data-id="element-148" data-mp="3douu75p10iwn" />
                  <span className="sr-only" data-id="element-149" data-mp="38q9s10q7rp5k">Enlarge image: {page.caption}</span>
                </button>
              </li>)}
          </ul>

          <button type="button" onClick={() => go(index + 1)} className={`hidden sm:flex ${ARROW}`} data-id="element-150" data-mp="2yzonyxrrtsf9">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-151" data-mp="2kzxg7erul89p" />
            <span className="sr-only" data-id="element-152" data-mp="2l29tvfqkowgy">Next image</span>
          </button>
        </div>

        <div className="mt-4 flex items-center justify-center gap-4" data-id="element-153" data-mp="2q13511ie0bdy">
          <button type="button" onClick={() => go(index - 1)} className={`sm:hidden ${ARROW}`} data-id="element-154" data-mp="3sb4woodmpwj9">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-155" data-mp="3oc4cp2jf0nuo" />
            <span className="sr-only" data-id="element-156" data-mp="3jj2x0sbdwka1">Previous image</span>
          </button>
          <div className="text-center" aria-live="polite" data-id="element-157" data-mp="3eljj5tilm9im">
            <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-pumpkin sm:text-[13px]" data-id="element-158" data-mp="3eixcrx7g201z">
              {insidePages[index].caption}
            </p>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted" data-id="element-159" data-mp="34m2enff321o8">
              {index + 1} / {TOTAL}
              <span aria-hidden="true" data-id="element-160" data-mp="2vmp1abnu8sy8">·</span>
              <Maximize2Icon className="h-3 w-3" strokeWidth={2} aria-hidden="true" data-id="element-161" data-mp="2vo05gcl742pv" />
              <span className="sm:hidden" data-id="element-162" data-mp="ytdi6faokyhc">Tap to enlarge</span>
              <span className="hidden sm:inline" data-id="element-163" data-mp="tu9chymytivr">Click to enlarge</span>
            </p>
          </div>
          <button type="button" onClick={() => go(index + 1)} className={`sm:hidden ${ARROW}`} data-id="element-164" data-mp="11etklda27z52">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-165" data-mp="16cv3ny7hjygj" />
            <span className="sr-only" data-id="element-166" data-mp="1b4d8wygjkl7b">Next image</span>
          </button>
        </div>
      </div>

      <ImageLightbox index={zoomed} onClose={() => setZoomed(null)} onChange={setZoomed} data-id="element-167" data-mp="1g2y9x03mb8wt" />
    </section>;
}
type ImageLightboxProps = {
  index: number | null;
  onClose: () => void;
  onChange: (i: number) => void;
};
function ImageLightbox({
  index,
  onClose,
  onChange
}: ImageLightboxProps) {
  const open = index !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index !== null) onChange((index + 1) % TOTAL);
      if (e.key === 'ArrowLeft' && index !== null) onChange((index - 1 + TOTAL) % TOTAL);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, index, onClose, onChange]);
  const page = index !== null ? insidePages[index] : null;
  return <AnimatePresence data-id="element-168" data-mp="cqpj8vya8uns">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={page.caption} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 z-50 flex flex-col bg-black/95" onClick={onClose} data-id="element-169" data-mp="cp4op6byxbpx">
          <div className="flex items-center justify-between px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-170" data-mp="3q5c2c86d4cq8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-pumpkin" data-id="element-171" data-mp="32eyjn89o3q8b">
              {page.caption}
              <span className="ml-3 font-medium text-muted" data-id="element-172" data-mp="2xfdhyq3453rw">
                {index + 1} / {TOTAL}
              </span>
            </p>
            <button type="button" onClick={onClose} autoFocus className="flex h-11 w-11 items-center justify-center rounded-full text-cream-100 transition-colors duration-150 ease-out hover:bg-night-700" data-id="element-173" data-mp="1hqntafk05pkn">
              <XIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-174" data-mp="1wnetus5ex7bu" />
              <span className="sr-only" data-id="element-175" data-mp="1wo6gveky1wqi">Close</span>
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center px-2 pb-4" data-id="element-176" data-mp="20ogm2p0wwj4b">
            <motion.img key={page.image.src} src={page.image.src} alt={page.image.alt} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} onClick={e => e.stopPropagation()} className="max-h-full w-auto max-w-full rounded-lg object-contain" data-id="element-177" data-mp="1mofj941371m7" />
          </div>

          <div className="flex items-center justify-center gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={e => e.stopPropagation()} data-id="element-178" data-mp="12xmvxmcrk333">
            <button type="button" onClick={() => onChange((index - 1 + TOTAL) % TOTAL)} className={ARROW} data-id="element-179" data-mp="y0d9mfko87jh">
              <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-180" data-mp="1qn7rd4q3sst9" />
              <span className="sr-only" data-id="element-181" data-mp="1qpahrpv64nyi">Previous image</span>
            </button>
            <button type="button" onClick={() => onChange((index + 1) % TOTAL)} className={ARROW} data-id="element-182" data-mp="1lowh4svwcgai">
              <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-183" data-mp="1gvl8cm7ixpfy" />
              <span className="sr-only" data-id="element-184" data-mp="1bxs25t680vfo">Next image</span>
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}